<template>
  <v-defaults-provider
    :defaults="{
      VBtn: {
        size: 'small',
        VIcon: {
          size: 'x-small',
        },
      },
    }"
  >
    <v-treeview
      v-model:opened="openedItems"
      base-color="primary"
      class="nav-drawer-treeview py-0"
      collapse-icon="far fa-angle-down"
      data-test-id="box-navigation-files"
      density="compact"
      expand-icon="far fa-angle-right"
      fluid
      hoverable
      :item-props="getItemProps"
      item-title="displayName"
      item-type="_type"
      item-value="nodeId"
      :items="deepBoxDeepBoxesBoxesFilesTreeStore.filesTree"
      :load-children="fetchChildren"
      loading-icon="far fa-loader"
      prepend-gap="12"
      rounded="lg"
      @click:open="onOpen"
    >
      <template #prepend="{ item, isOpen }">
        <v-progress-circular
          v-if="item.type === 'folder' && loadingItems.includes(item.nodeId)"
          color="primary"
          indeterminate="disable-shrink"
          size="16"
          width="2"
        />
        <v-icon
          v-else-if="item.type === 'folder'"
          class="folder-icon"
          color="primary"
        >
          {{ isOpen ? 'far fa-folder-open' : 'far fa-folder' }}
        </v-icon>
        <v-icon v-else class="mx-1" size="small">
          <NodeIcon
            :mime-type="item.mimeType"
            :name="item.name"
            :type="item.type"
          />
        </v-icon>
      </template>
    </v-treeview>
  </v-defaults-provider>
</template>

<script setup lang="ts">
import { MIME_TYPES_APPLICATION } from '@deepcloud/deep-ui-utils'
import { useDeepBoxDeepBoxesBoxesFilesTreeStore } from '@/stores/deepbox/deepboxes/boxes/files-tree.ts'
import NodeIcon from '@/components/node/NodeIcon.vue'
import type { FileTree } from '@/types/file-tree.ts'

const props = defineProps({
  typeId: {
    type: String,
    required: true,
  },
  boxId: {
    type: String,
    required: true,
  },
})

type BoxTreeViewItemPayload = {
  event: DragEvent
  item: FileTree
}

const emit = defineEmits<{
  (e: 'item:dragend', payload: BoxTreeViewItemPayload): void
  (e: 'item:dragenter', payload: BoxTreeViewItemPayload): void
  (e: 'item:dragleave', payload: BoxTreeViewItemPayload): void
  (e: 'item:dragover', payload: BoxTreeViewItemPayload): void
  (e: 'item:dragstart', payload: BoxTreeViewItemPayload): void
  (e: 'item:drop', payload: BoxTreeViewItemPayload): void
}>()

const { t } = useI18n()

const deepBoxDeepBoxesBoxesFilesTreeStore =
  useDeepBoxDeepBoxesBoxesFilesTreeStore()

const openedItems = ref<string[]>([])
const loadingItems = ref<string[]>([])

watch(
  () => props.boxId,
  () => {
    openedItems.value = []
    loadingItems.value = []
  },
)

onUnmounted(() => {
  deepBoxDeepBoxesBoxesFilesTreeStore.$reset()
})

async function fetchChildren(item: unknown) {
  const itemFileTree = item as FileTree

  let res

  if (itemFileTree.mimeType === MIME_TYPES_APPLICATION.MIME_TYPE_FILES) {
    res = await deepBoxDeepBoxesBoxesFilesTreeStore.fetchFilesTree({
      typeId: props.typeId,
      boxId: props.boxId,
      parent: itemFileTree,
    })
  } else {
    res = await deepBoxDeepBoxesBoxesFilesTreeStore.fetchFilesTreeChildren({
      typeId: props.typeId,
      boxId: props.boxId,
      nodeId: itemFileTree.nodeId,
      parent: itemFileTree,
    })
  }

  itemFileTree.children = res.data.children as FileTree[]
}

type TreeViewItemOpen = {
  id: unknown
  value: boolean
  path: unknown[]
}

function onOpen(item: TreeViewItemOpen) {
  if (item.path.length === 1) {
    deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened = item.value
  }
}

function isRootItem(item: FileTree) {
  return item.name === 'files'
}

async function openItem(item: FileTree) {
  if (!item.children || item.children.length === 0) {
    loadingItems.value = [...loadingItems.value, item.nodeId]
    try {
      await fetchChildren(item)
    } finally {
      loadingItems.value = loadingItems.value.filter(
        (nodeId) => nodeId !== item.nodeId,
      )
    }
  }

  if (!openedItems.value.includes(item.nodeId)) {
    openedItems.value = [...openedItems.value, item.nodeId]
  }

  if (isRootItem(item)) {
    deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened = true
  }
}

defineExpose({ openItem })

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getItemProps(item: Record<string, any>) {
  const itemFileTree = item as FileTree

  const isRoot = isRootItem(itemFileTree)

  return {
    id: itemFileTree.nodeId,
    ['data-node-id']: itemFileTree.nodeId,
    ['data-test-id']: `tree-folder-${item.name}`,
    rounded: 'lg',
    draggable: isRoot ? 'false' : 'true',
    exact: true,
    title: isRoot
      ? t('box_details.details_drawer.files')
      : item.displayName || item.name,
    to: isRoot
      ? {
          name: 'organization-types-type-boxes-box-files',
        }
      : {
          name: 'organization-types-type-boxes-box-files-node',
          params: { node: itemFileTree.nodeId },
        },
    onDragstart: (event: DragEvent) =>
      emit('item:dragstart', { event, item: itemFileTree }),
    onDragend: (event: DragEvent) =>
      emit('item:dragend', { event, item: itemFileTree }),
    onDragenter: (event: DragEvent) =>
      emit('item:dragenter', { event, item: itemFileTree }),
    onDragleave: (event: DragEvent) =>
      emit('item:dragleave', { event, item: itemFileTree }),
    onDragover: (event: DragEvent) => {
      event.preventDefault()
      emit('item:dragover', { event, item: itemFileTree })
    },
    onDrop: (event: DragEvent) =>
      emit('item:drop', { event, item: itemFileTree }),
  }
}
</script>

<style scoped lang="scss">
.nav-drawer-treeview {
  overflow-x: auto;
  --indent-padding: -4px;

  :deep(.v-treeview-group) {
    overflow-x: auto;
  }

  & > :deep(.v-list-group) > .v-list-item .v-list-item-action {
    margin-inline-start: -16px !important;
    margin-inline-end: 4px;
  }

  :deep(.folder-icon) {
    opacity: 1;
  }

  /*
  *  This is needed in because "base-color" prop also set the text color
  */
  :deep(.v-list-item-title) {
    color: rgba(
      var(--v-theme-on-surface),
      var(--v-high-emphasis-opacity)
    ) !important;
  }

  :deep(.v-list-group) > .v-list-item {
    margin-top: 0;
  }

  /*
  *  This is needed in order to always have the item bg when is active
  * for some reason vuetify override it with 0
  */
  :deep(
    .v-list-group__header.v-list-item--active:not(
        .v-treeview-item--activatable-group-activator
      ):not(:focus-visible)
      .v-list-item__overlay
  ) {
    opacity: calc(
      var(--v-activated-opacity) * var(--v-theme-overlay-multiplier)
    );
  }

  :deep(.v-list-group) {
    margin-top: 0;
  }
}
</style>
